/**
 * REDEX Admin - Discovery Dashboard Page
 * 
 * Embeds the full Discovery dashboard with 3D visualization
 * The standalone discovery app is served at /discovery-app/
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Fullscreen,
  FullscreenExit,
  OpenInNew,
  Explore,
} from '@mui/icons-material';

// Discovery app URL
const DISCOVERY_APP_URL = process.env.NODE_ENV === 'production' 
  ? 'https://www.redexct.com/discovery-app/'
  : 'http://localhost:3106';

export default function DiscoveryPage() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState<'embedded' | 'standalone'>('embedded');

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleOpenStandalone = () => {
    window.open(DISCOVERY_APP_URL, '_blank');
  };

  if (viewMode === 'standalone') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          The Discovery Dashboard is open in a new tab. 
          <Typography 
            component="a" 
            onClick={() => setViewMode('embedded')}
            sx={{ ml: 1, cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }}
          >
            Switch to embedded view
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        height: isFullscreen ? '100vh' : 'calc(100vh - 64px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 9999 : 'auto',
        bgcolor: '#0a0a0f',
      }}
    >
      {/* Header Bar */}
      <Paper 
        elevation={0}
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          bgcolor: 'rgba(15,15,20,0.95)',
          borderRadius: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Explore sx={{ color: '#00d4aa' }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Discovery Dashboard
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              bgcolor: 'rgba(245,158,11,0.15)', 
              color: '#f59e0b',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              border: '1px solid rgba(245,158,11,0.3)',
            }}
          >
            Awaiting Execution
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Open in new tab">
            <IconButton onClick={handleOpenStandalone} size="small" sx={{ color: 'grey.400' }}>
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
            <IconButton onClick={handleFullscreen} size="small" sx={{ color: 'grey.400' }}>
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Embedded Dashboard */}
      <Box 
        sx={{ 
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <iframe
          src={DISCOVERY_APP_URL}
          title="REDEX Discovery Dashboard"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
          }}
          allow="fullscreen"
        />
      </Box>
    </Box>
  );
}
